import Loading from "@/components/loading";
import { apiDeleteRequest, apiGetRequest, apiPostRequest } from "@/functions/requests";
import { FetchUserAttributesOutput, fetchUserAttributes, getCurrentUser, updateUserAttribute } from "@aws-amplify/auth";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { Avatar, Button, Card, CardBody, CardFooter, CardHeader, Center, Heading, Flex, Text, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Box, Badge, useColorModeValue, Input, IconButton, useToast, Tooltip, SimpleGrid, Spinner } from "@chakra-ui/react";
import { AppMeta } from "@xnav/types/src/common";
import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faCheck, faEdit, faTimes, faTrash, faUserShield, faCircle, faBook } from "@fortawesome/free-solid-svg-icons";

export default function Profile() {
	const { user, signOut } = useAuthenticator();
	const [userAttributes, setUserAttributes] = useState<FetchUserAttributesOutput>();
	const [deletionStatus, setDeletionStatus] = useState<string>();
	const { isOpen, onOpen, onClose } = useDisclosure();
	const toast = useToast();

	const [isEditingUsername, setIsEditingUsername] = useState(false);
	const [newUsername, setNewUsername] = useState("");
	const [isUpdating, setIsUpdating] = useState(false);
	const [signedPictureUrl, setSignedPictureUrl] = useState<string>();
	const [userRole, setUserRole] = useState<string>("Loading...");
	const [userOrg, setUserOrg] = useState<string>("");
	const [coursesCount, setCoursesCount] = useState<number | null>(null);

	const fileInputRef = useRef<HTMLInputElement>(null);

	const bgColor = useColorModeValue("white", "gray.800");
	const cardBg = useColorModeValue("white", "gray.800");
	const gradientBg = useColorModeValue("linear(to-br, teal.50, green.50)", "linear(to-br, gray.800, gray.900)");

	async function loadSignedPicture(pictureValue: string | undefined) {
		if (!pictureValue) return;
		
		// If it's an S3 URL from our bucket, extract the fileKey
		let mediaKeyId = pictureValue;
		if (pictureValue.includes("s3.us-west-2.amazonaws.com")) {
			const urlObj = new URL(pictureValue);
			mediaKeyId = urlObj.pathname.substring(1); // Remove leading slash
		} else if (pictureValue.startsWith("http")) {
			// Probably a Google/social login picture, just use it directly
			setSignedPictureUrl(pictureValue);
			return;
		}

		try {
			const response = await apiGetRequest<undefined, { signedUrl: string }>(`/get-profile-image?mediaKeyId=${encodeURIComponent(mediaKeyId)}`);
			if (response.signedUrl) {
				setSignedPictureUrl(response.signedUrl);
			}
		} catch (e) {
			console.error("Failed to load signed picture URL", e);
		}
	}

	useEffect(() => {
		fetchUserAttributes().then(attrs => {
			setUserAttributes(attrs);
			setNewUsername(attrs.preferred_username || attrs.email || "");
			loadSignedPicture(attrs.picture);
		});

		if (user?.signInDetails?.loginId) {
			apiGetRequest<undefined, { role: string, org?: string }>(`/users/${user.signInDetails.loginId}`)
				.then(data => {
					setUserRole(data?.role || 'Instructor');
					setUserOrg(data?.org || '');
				})
				.catch(() => {
					setUserRole('Instructor');
					setUserOrg('');
				});
		}

		apiGetRequest<string, AppMeta[]>('/apps')
			.then(apps => setCoursesCount(apps.length))
			.catch(() => setCoursesCount(0));
	}, [user]);

	async function deleteProfile() {
		setDeletionStatus("Removing all deployed apps...");
		const apps: AppMeta[] = await apiGetRequest<string, AppMeta[]>('/apps');
		const deleteAppsPromises = apps.map(app => apiDeleteRequest<undefined>(`/deploy/${app.id}`));
		await Promise.all(deleteAppsPromises);

		setDeletionStatus("Removing all edited apps...");
		const deleteEditPromises = apps.map(app => apiDeleteRequest<AppMeta>(`/apps/${app.id}`));
		await Promise.all(deleteEditPromises);

		setDeletionStatus("Deleting profile...");
		const { username } = await getCurrentUser();
		await apiDeleteRequest<undefined>(`/account/${username}`);
		signOut();
	}

	async function handleSaveUsername() {
		if (!newUsername.trim()) return;
		setIsUpdating(true);
		try {
			await updateUserAttribute({
				userAttribute: {
					attributeKey: 'preferred_username',
					value: newUsername
				}
			});
			setUserAttributes(prev => prev ? { ...prev, preferred_username: newUsername } : prev);
			setIsEditingUsername(false);
			toast({ title: "Username updated", status: "success", duration: 3000, isClosable: true });
		} catch (error: any) {
			toast({ title: "Failed to update username", description: error.message, status: "error", duration: 5000, isClosable: true });
		} finally {
			setIsUpdating(false);
		}
	}

	async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		if (!file) return;

		setIsUpdating(true);
		try {
			const reader = new FileReader();
			reader.onloadend = async () => {
				const base64Data = (reader.result as string).split(',')[1];
				
				const response = await apiPostRequest<any, { imageUrl: string, fileKey: string }>('/upload-image', {
					fileName: file.name,
					fileContent: base64Data
				});

				if (response.fileKey) {
					await updateUserAttribute({
						userAttribute: {
							attributeKey: 'picture',
							value: response.fileKey
						}
					});
					setUserAttributes(prev => prev ? { ...prev, picture: response.fileKey } : prev);
					loadSignedPicture(response.fileKey);
					toast({ title: "Profile picture updated", status: "success", duration: 3000, isClosable: true });
				}
			};
			reader.readAsDataURL(file);
		} catch (error: any) {
			toast({ title: "Failed to upload picture", description: error.message, status: "error", duration: 5000, isClosable: true });
		} finally {
			setIsUpdating(false);
		}
	}

	if (!userAttributes) return (
		<Center flex={1} w='100%' bg={gradientBg}>
			<Card>
				<CardHeader>
					<Center><Heading>Loading...</Heading></Center>
				</CardHeader>
				<CardBody>
					<Loading message="Fetching user attributes" />
				</CardBody>
			</Card>
		</Center>
	);

	if (deletionStatus) return (
		<Center flex={1} w='100%' bg={gradientBg}>
			<Card>
				<CardHeader>
					<Center><Heading>Deleting Account...</Heading></Center>
				</CardHeader>
				<CardBody>
					<Loading message={deletionStatus} />
				</CardBody>
			</Card>
		</Center>
	);

	return (
		<Center flex={1} w='100%' bg={gradientBg} p={8}>
			<Card 
				variant="elevated" 
				w="full" 
				maxW="500px" 
				borderRadius="2xl" 
				boxShadow="2xl" 
				bg={cardBg}
				overflow="hidden"
			>
				<Box 
					h="120px" 
					w="100%" 
					bgGradient="linear(to-r, #0284c7, #0ea5e9, #06b6d4, #10b981)" 
				/>
				
				<Flex flexDir="column" alignItems="center" mt="-60px" px={6} pb={6}>
					<Box position="relative">
						<Avatar
							size="2xl"
							name={userAttributes.preferred_username || userAttributes.email}
							src={signedPictureUrl}
							border="4px solid"
							borderColor={cardBg}
							shadow="lg"
							bg="white"
							color="blue.500"
						/>
						<Tooltip label="Change Profile Picture" placement="top">
							<IconButton
								aria-label="Upload picture"
								icon={<FontAwesomeIcon icon={faCamera} />}
								size="sm"
								colorScheme="blue"
								rounded="full"
								position="absolute"
								bottom={1}
								right={1}
								shadow="md"
								isLoading={isUpdating}
								onClick={() => fileInputRef.current?.click()}
							/>
						</Tooltip>
						<input 
							type="file" 
							accept="image/*" 
							ref={fileInputRef} 
							style={{ display: 'none' }} 
							onChange={handleFileChange} 
						/>
					</Box>

					<Flex flexDir="column" alignItems="center" mt={4} w="100%">
						{isEditingUsername ? (
							<Flex gap={2} w="100%" maxW="300px" mt={1}>
								<Input 
									value={newUsername} 
									onChange={e => setNewUsername(e.target.value)} 
									autoFocus 
									size="sm"
									borderRadius="md"
								/>
								<IconButton 
									aria-label="Save username" 
									icon={<FontAwesomeIcon icon={faCheck} />} 
									size="sm" 
									colorScheme="green" 
									isLoading={isUpdating}
									onClick={handleSaveUsername}
								/>
								<IconButton 
									aria-label="Cancel" 
									icon={<FontAwesomeIcon icon={faTimes} />} 
									size="sm" 
									variant="ghost" 
									onClick={() => {
										setIsEditingUsername(false);
										setNewUsername(userAttributes.preferred_username || userAttributes.email || "");
									}}
								/>
							</Flex>
						) : (
							<Flex alignItems="center" gap={3} mt={1}>
								<Heading size="lg" fontWeight="extrabold" letterSpacing="tight">
									{userAttributes.preferred_username || userAttributes.email?.split('@')[0]}
								</Heading>
								<Tooltip label="Edit Username" placement="top">
									<IconButton 
										aria-label="Edit username" 
										icon={<FontAwesomeIcon icon={faEdit} />} 
										size="xs" 
										variant="ghost" 
										color="gray.400"
										_hover={{ color: "blue.500" }}
										onClick={() => setIsEditingUsername(true)}
									/>
								</Tooltip>
							</Flex>
						)}
						<Flex align="center" gap={2} mt={3}>
							<Badge 
								colorScheme={userRole === 'Super Admin' ? 'green' : userRole === 'Admin' ? 'blue' : 'green'} 
								variant="subtle" 
								px={3} py={1} borderRadius="full" fontSize="xs" fontWeight="bold"
							>
								<FontAwesomeIcon icon={faUserShield} style={{ marginRight: '6px' }} />
								{userRole === 'Loading...' ? 'Fetching Role...' : userRole}
							</Badge>
							{userOrg && (
								<Badge 
									colorScheme="orange" 
									variant="subtle" 
									px={3} py={1} borderRadius="full" fontSize="xs" fontWeight="bold"
								>
									<FontAwesomeIcon icon={faCircle} style={{ marginRight: '6px', fontSize: '8px', verticalAlign: 'middle' }} />
									{userOrg}
								</Badge>
							)}
						</Flex>
					</Flex>
				</Flex>

				<CardBody pt={0} px={8}>
					<Flex flexDir='column' gap={4}>
						<SimpleGrid columns={2} spacing={4}>
							<Box p={4} bg={useColorModeValue("gray.50", "whiteAlpha.50")} borderRadius="xl" borderWidth="1px" borderColor={useColorModeValue("gray.100", "whiteAlpha.100")}>
								<Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={1}>Account Status</Text>
								<Flex alignItems="center" gap={2}>
									<FontAwesomeIcon icon={faCircle} color="#38A169" style={{ fontSize: '10px' }} />
									<Text fontSize="md" fontWeight="bold" color={useColorModeValue("green.600", "green.400")}>Active</Text>
								</Flex>
							</Box>
							<Box p={4} bg={useColorModeValue("gray.50", "whiteAlpha.50")} borderRadius="xl" borderWidth="1px" borderColor={useColorModeValue("gray.100", "whiteAlpha.100")}>
								<Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={1}>Managed Courses</Text>
								<Flex alignItems="center" gap={2}>
									<FontAwesomeIcon icon={faBook} color="#0ea5e9" />
									<Text fontSize="md" fontWeight="bold" color={useColorModeValue("cyan.700", "teal.300")}>
										{coursesCount === null ? <Spinner size="xs" /> : coursesCount}
									</Text>
								</Flex>
							</Box>
						</SimpleGrid>

						<Box p={4} bg={useColorModeValue("gray.50", "whiteAlpha.50")} borderRadius="xl" borderWidth="1px" borderColor={useColorModeValue("gray.100", "whiteAlpha.100")}>
							<Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={1}>Email Address</Text>
							<Flex alignItems="center" justify="space-between">
								<Text fontSize="md" fontWeight="medium" color={useColorModeValue("gray.800", "white")}>{userAttributes.email}</Text>
								{userAttributes.email_verified === 'true' && <Badge colorScheme="green" borderRadius="full" px={2} py={0.5} textTransform="none" fontSize="xs">Verified</Badge>}
							</Flex>
						</Box>

						{userOrg && (
							<Box p={4} bg={useColorModeValue("gray.50", "whiteAlpha.50")} borderRadius="xl" borderWidth="1px" borderColor={useColorModeValue("gray.100", "whiteAlpha.100")}>
								<Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={1}>Organization</Text>
								<Text fontSize="md" fontWeight="bold" color="orange.500">{userOrg}</Text>
							</Box>
						)}

						<Box p={4} bg={useColorModeValue("gray.50", "whiteAlpha.50")} borderRadius="xl" borderWidth="1px" borderColor={useColorModeValue("gray.100", "whiteAlpha.100")}>
							<Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={1}>User ID</Text>
							<Text fontSize="xs" fontFamily="mono" color="gray.600" wordBreak="break-all">{userAttributes.sub}</Text>
						</Box>
					</Flex>
				</CardBody>

				<CardFooter px={8} pb={8} pt={2}>
					<Flex w='100%' justify="space-between" align="center" gap={4}>
						<Button
							variant='ghost'
							colorScheme="gray"
							onClick={signOut}
							_hover={{ bg: useColorModeValue("gray.100", "whiteAlpha.100") }}
							flex={1}
							borderRadius="lg"
						>
							Logout
						</Button>
						<Button
							colorScheme="red"
							variant="outline"
							onClick={onOpen}
							_hover={{ bg: "red.50", color: "red.600" }}
							flex={1}
							borderRadius="lg"
							leftIcon={<FontAwesomeIcon icon={faTrash} />}
						>
							Delete Profile
						</Button>
					</Flex>
				</CardFooter>
			</Card>

			{/* Confirmation Modal */}
			<Modal isOpen={isOpen} onClose={onClose} isCentered>
				<ModalOverlay backdropFilter="blur(4px)" />
				<ModalContent borderRadius="xl">
					<ModalHeader pb={0}>Confirm Account Deletion</ModalHeader>
					<ModalBody py={6}>
						<Text color={useColorModeValue("gray.600", "gray.300")}>
							Deleting your account is <Text as="span" fontWeight="bold" color="red.500">irreversible</Text>. All your apps and data will be permanently removed. Are you sure you want to continue?
						</Text>
					</ModalBody>
					<ModalFooter borderTopWidth="1px" borderColor={useColorModeValue("gray.100", "whiteAlpha.100")}>
						<Flex gap={3} w="100%">
							<Button variant="ghost" onClick={onClose} flex={1}>
								Cancel
							</Button>
							<Button colorScheme="red" onClick={deleteProfile} flex={1}>
								Yes, Delete Account
							</Button>
						</Flex>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</Center>
	);
}
